import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";

const NAV_LINKS = [
    { label: "Features", hash: "#features" },
    { label: "How It Works", hash: "#how-it-works" },
    { label: "Shopping", hash: "#shopping" },
    { label: "About", hash: "#about" },
    { label: "Contact", hash: "#contact" },
];

const NAV_HEIGHT_PX = 72;

export const scrollToHash = (hash) => {
    if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -NAV_HEIGHT_PX });
    else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

export const Anchor = ({ hash, children, className, testId, onDone }) => {
    const { pathname } = useLocation();
    const onHome = pathname === "/";
    return (
        <a
            href={onHome ? hash : `/${hash}`}
            data-testid={testId}
            className={className}
            onClick={(e) => {
                if (onHome) {
                    e.preventDefault();
                    scrollToHash(hash);
                }
                onDone?.();
            }}
        >
            {children}
        </a>
    );
};

export default function Navbar() {
    const [open, setOpen] = useState(false);

    return (
        <header data-testid="site-navbar" className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
            <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between" aria-label="Main navigation">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group" aria-label="LookAura home">
                    <Sparkles className="w-5 h-5 text-amber-500 transition-transform duration-500 group-hover:rotate-90" />
                    <span className="font-serif-display text-xl tracking-tight text-[#FAFAFA]">LookAura</span>
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <Anchor key={l.hash} hash={l.hash} testId={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-zinc-400 hover:text-amber-400 transition-colors duration-300">
                            {l.label}
                        </Anchor>
                    ))}
                    <Anchor
                        hash="#app"
                        testId="nav-get-started-button"
                        className="text-sm font-semibold px-5 py-2.5 rounded-full border border-amber-600/70 text-amber-400 hover:bg-amber-500 hover:text-[#050505] transition-colors duration-300"
                    >
                        Get Started
                    </Anchor>
                </div>

                <button data-testid="nav-mobile-menu-button" className="md:hidden text-zinc-300" onClick={() => setOpen(!open)} aria-label="Toggle menu" aria-expanded={open}>
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </nav>

            {open && (
                <div data-testid="nav-mobile-menu" className="md:hidden border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur-xl px-6 py-6 flex flex-col gap-5">
                    {NAV_LINKS.map((l) => (
                        <Anchor key={l.hash} hash={l.hash} testId={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-base text-zinc-300 hover:text-amber-400 transition-colors" onDone={() => setOpen(false)}>
                            {l.label}
                        </Anchor>
                    ))}
                    <Anchor hash="#app" testId="nav-mobile-get-started-button" className="text-base font-semibold text-amber-400" onDone={() => setOpen(false)}>
                        Get Started
                    </Anchor>
                </div>
            )}
        </header>
    );
}
